import _cloneDeep from 'lodash.clonedeep'
import _isEqual from 'lodash.isequal'
import { formatDate, formatTime } from '@smarttransit/common'
import {
  getPayoutSettings,
  getPayoutSettingsFieldLabel,
  PAYOUT_SETTINGS_FIELD_LABELS,
  PAYOUT_SETTINGS_FIELD_TOOLTIPS,
  updatePayoutSettings
} from '@/services/st-settings-service'
import { addAlert } from '@/utilities/helpers'

const defaultAgencyId = 'STRANSITGHA'
const payoutAgencyOptions = [{ text: defaultAgencyId, value: defaultAgencyId }]

function toFiniteNumberOrNull (value) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function toWholeNumberOrNull (value) {
  const parsed = Number(value)

  if (!Number.isFinite(parsed) || !Number.isInteger(parsed)) {
    return null
  }

  return parsed
}

function toCentsOrNull (value) {
  const parsed = Number(value)

  if (!Number.isFinite(parsed) || !Number.isInteger(parsed)) {
    return null
  }

  return parsed
}

function percentDecimalToWholeNumberOrNull (value) {
  const parsed = toFiniteNumberOrNull(value)

  if (parsed === null) {
    return null
  }

  return Math.round(parsed * 100)
}

function percentWholeNumberToDecimalOrNull (value) {
  const parsed = toWholeNumberOrNull(value)

  if (parsed === null) {
    return null
  }

  return Number((parsed / 100).toFixed(2))
}

export default {
  name: 'advanced-payout-settings',
  props: {
    signedInUser: Object
  },
  data () {
    return {
      apiInProgress: false,
      currentSettings: null,
      originalCurrentSettings: null,
      payoutFieldLabels: PAYOUT_SETTINGS_FIELD_LABELS,
      payoutFieldTooltips: PAYOUT_SETTINGS_FIELD_TOOLTIPS,
      payoutAgencyOptions
    }
  },
  computed: {
    isCurrentSettingsEdited () {
      return this.currentSettings && !_isEqual(this.currentSettings, this.originalCurrentSettings)
    }
  },
  mounted () {
    this.loadSettings()
  },
  methods: {
    normalizeSettings (setting) {
      return {
        id: setting.id,
        agencyId: defaultAgencyId,
        smarttransytFee: toCentsOrNull(setting.smarttransytFee),
        smarttransytFeePercentage: percentDecimalToWholeNumberOrNull(setting.smarttransytFeePercentage),
        beaconFee: toCentsOrNull(setting.beaconFee),
        beaconMaintenanceFee: toCentsOrNull(setting.beaconMaintenanceFee),
        perDayDriverFee: toCentsOrNull(setting.perDayDriverFee),
        perKwHFee: toCentsOrNull(setting.perKwHFee),
        dateUpdated: setting.dateUpdated,
        dateUpdatedLabel: setting.dateUpdated ? `${formatDate(setting.dateUpdated)}, ${formatTime(setting.dateUpdated)}` : ''
      }
    },
    validateCurrentSettings () {
      if (!this.currentSettings) {
        return false
      }

      const feeFields = ['smarttransytFee', 'beaconFee', 'beaconMaintenanceFee', 'perDayDriverFee', 'perKwHFee']

      for (let i = 0; i < feeFields.length; i += 1) {
        const fieldName = feeFields[i]
        const value = toCentsOrNull(this.currentSettings[fieldName])
        if (value === null || value < 0) {
          const fieldLabel = getPayoutSettingsFieldLabel(fieldName)
          alert(`${fieldLabel} must be a valid currency value greater than or equal to 0`)
          return false
        }
      }

      const percentValue = toWholeNumberOrNull(this.currentSettings.smarttransytFeePercentage)

      if (percentValue === null || percentValue < 0) {
        const percentLabel = getPayoutSettingsFieldLabel('smarttransytFeePercentage')
        alert(`${percentLabel} must be a whole number greater than or equal to 0`)
        return false
      }

      return true
    },
    async loadSettings () {
      this.apiInProgress = true
      try {
        const settings = await getPayoutSettings({ agencyId: defaultAgencyId })

        if (!settings) {
          this.currentSettings = null
          this.originalCurrentSettings = null
          addAlert({ message: 'No payout settings found for STRANSITGHA', type: 'error' })
          return
        }

        this.currentSettings = this.normalizeSettings(settings)
        this.originalCurrentSettings = _cloneDeep(this.currentSettings)
      } catch (err) {
        addAlert({ message: err, type: 'error' })
      } finally {
        this.apiInProgress = false
      }
    },
    cancelEdit () {
      if (!this.currentSettings) {
        return
      }
      if (!this.isCurrentSettingsEdited) {
        return
      }

      if (confirm('Discard unsaved payout settings changes?')) {
        this.currentSettings = _cloneDeep(this.originalCurrentSettings)
      }
    },
    async saveSettings () {
      if (!this.validateCurrentSettings() || !this.currentSettings) {
        return
      }

      this.apiInProgress = true
      try {
        const updatedSettings = await updatePayoutSettings({
          agencyId: this.currentSettings.agencyId || defaultAgencyId,
          smarttransytFee: toCentsOrNull(this.currentSettings.smarttransytFee),
          smarttransytFeePercentage: percentWholeNumberToDecimalOrNull(this.currentSettings.smarttransytFeePercentage),
          beaconFee: toCentsOrNull(this.currentSettings.beaconFee),
          beaconMaintenanceFee: toCentsOrNull(this.currentSettings.beaconMaintenanceFee),
          perDayDriverFee: toCentsOrNull(this.currentSettings.perDayDriverFee),
          perKwHFee: toCentsOrNull(this.currentSettings.perKwHFee)
        })

        this.currentSettings = this.normalizeSettings(updatedSettings)
        this.originalCurrentSettings = _cloneDeep(this.currentSettings)
        addAlert({ message: 'Successfully updated payout settings', type: 'success' })
      } catch (err) {
        addAlert({ message: err, type: 'error' })
      } finally {
        this.apiInProgress = false
      }
    }
  }
}
